import {useEffect} from 'react'
import {useSWRConfig} from 'swr'

import type {Iri} from '../../@types/Api'
import type {HydraCollectionMember} from '../../@types/hydra/HydraCollectionMember'
import type {HydraResponse} from '../../@types/hydra/HydraResponse'
import {setInvalidationHandler} from '../utils/invalidation'
import {createCollectionIri} from '../utils/iri'
import {sleep} from '../utils/sleep'

import {useUserHelperGetCurrent} from './entity/useUser'
import {useEvent} from './useEvent'

const MERCURE_GRACE = 300
const MERCURE_POLL = 50

const mercureSeen = new Map<string, number>()

export function markMercureTags(tags: string[]): void {
  const now = Date.now()
  for (const [tag, seen] of mercureSeen) {
    if (seen < now - MERCURE_GRACE) {
      mercureSeen.delete(tag)
    }
  }
  for (const tag of tags) {
    mercureSeen.set(tag, now)
  }
}

export function useInstallInvalidationHandler(): void {
  const mutateTags = useMutateTags()

  const handle = useEvent((tags: string[]): void => {
    void (async () => {
      const since = Date.now()
      const deadline = since + MERCURE_GRACE
      let pending = tags

      for (;;) {
        pending = pending.filter((tag) => (mercureSeen.get(tag) ?? 0) < since)
        if (pending.length === 0 || Date.now() >= deadline) {
          break
        }
        await sleep(MERCURE_POLL)
      }

      if (pending.length > 0) {
        await mutateTags(pending)
      }
    })()
  })

  useEffect(() => setInvalidationHandler(handle), [handle])
}

interface Revalidation {
  promise: Promise<unknown>
  // A signal arrived while the request ran: its body may predate that write.
  stale: boolean
}

// SWR cannot dedup these: `mutate(key)` drops the key's dedup markers on purpose.
const inFlight = new Map<string, Revalidation>()

function revalidate(key: string, mutate: (key: string) => Promise<unknown>): Promise<unknown> {
  const running = inFlight.get(key)
  if (running) {
    running.stale = true
    return running.promise
  }

  const revalidation: Revalidation = {promise: Promise.resolve(), stale: false}
  revalidation.promise = mutate(key).finally(() => {
    inFlight.delete(key)
    return revalidation.stale ? revalidate(key, mutate) : undefined
  })
  inFlight.set(key, revalidation)

  return revalidation.promise
}

export function useMutateTags(): (tags: string[]) => Promise<void> {
  const {cache, mutate} = useSWRConfig()

  return useEvent(async (tags: string[]): Promise<void> => {
    const tagSet = new Set(tags)
    const mutations = []
    for (const key of cache.keys()) {
      const state = cache.get(key)
      const cacheTags = state?.data?.['@tags']
      if (!cacheTags) {
        continue
      }

      if (!cacheTags.find((value: string) => tagSet.has(value))) {
        continue
      }

      mutations.push(revalidate(key, mutate))
    }

    await Promise.all(mutations)
  })
}

/** See docs/pwa-patterns.md#cache-invalidation. */
export function useMutateResource(): (resourceIri: Iri, item?: HydraCollectionMember) => Promise<void> {
  const {user} = useUserHelperGetCurrent()
  const {cache, mutate} = useSWRConfig()

  return useEvent(async (resourceIri: Iri, item?: HydraCollectionMember): Promise<void> => {
    if (item === undefined) {
      await mutate(resourceIri)

      return
    }

    // A write answers without `xkey`: keep the tags the entry was read with, or no invalidation reaches it again.
    const cachedTags: string[] | undefined = cache.get(resourceIri)?.data?.['@tags']
    const answeredTags = (item as Partial<Pick<HydraResponse, '@tags'>>)['@tags']
    const tagged = answeredTags?.length || !cachedTags ? item : {...item, '@tags': cachedTags}
    const mutations = [mutate(resourceIri, tagged, {revalidate: false})]
    // `/me` holds a second copy of the same body, under a key no `member` scan can reach.
    if (user?.['@id'] === resourceIri) {
      mutations.push(mutate(createCollectionIri('me')))
    }
    for (const key of cache.keys()) {
      const data = cache.get(key)?.data
      const member = data?.member
      if (!Array.isArray(member) || !member.some((row: HydraCollectionMember) => row?.['@id'] === resourceIri)) {
        continue
      }

      mutations.push(mutate(
        key,
        {...data, member: member.map((row: HydraCollectionMember) => row?.['@id'] === resourceIri ? {...row, ...item} : row)},
        {revalidate: false},
      ))
    }

    await Promise.all(mutations)
  })
}
